/**
 * ワクチン期限アラート判定ロジック
 *
 * 狂犬病ワクチンを中心に、期限切れ・期限間近を判定する。
 * - 期限 90 日前 〜 期限当日 → 「期限間近」（黄色）
 * - 期限超過 → 「期限切れ」（赤色）
 */
import { VaccinationRecord } from '@/types/vaccination';

/** アラートレベル */
export type VaccinationAlertLevel = 'overdue' | 'upcoming' | 'none';

/** アラート間近とみなす日数（狂犬病 3 ヶ月前） */
export const VACCINATION_UPCOMING_DAYS = 90;

export interface VaccinationAlert {
  level: VaccinationAlertLevel;
  /** 期限までの残り日数（負数は超過日数） */
  daysUntilDue: number;
  /** 判定対象となった記録 */
  record: VaccinationRecord | null;
}

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * 2 つの日付の差（日数、切り捨て）を返す
 */
const diffInDays = (from: Date, to: Date): number => {
  const fromMidnight = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const toMidnight = new Date(to.getFullYear(), to.getMonth(), to.getDate());
  return Math.round((toMidnight.getTime() - fromMidnight.getTime()) / MS_PER_DAY);
};

/**
 * 期限日からアラートレベルを判定
 */
export const getAlertLevelFromDueDate = (
  dueDate: Date,
  today: Date = new Date(),
  upcomingDays: number = VACCINATION_UPCOMING_DAYS
): { level: VaccinationAlertLevel; daysUntilDue: number } => {
  const daysUntilDue = diffInDays(today, dueDate);

  if (daysUntilDue < 0) {
    return { level: 'overdue', daysUntilDue };
  }
  if (daysUntilDue <= upcomingDays) {
    return { level: 'upcoming', daysUntilDue };
  }
  return { level: 'none', daysUntilDue };
};

/**
 * 接種記録一覧から、最も注意が必要なアラートを返す。
 *
 * 各種類ごとに最新（次回期限が最も先）の記録を採用し、
 * その期限でアラートを判定。最も深刻なもの（期限切れ > 期限間近）を返す。
 */
export const getDogVaccinationAlert = (
  records: VaccinationRecord[],
  today: Date = new Date()
): VaccinationAlert => {
  const none: VaccinationAlert = { level: 'none', daysUntilDue: Infinity, record: null };

  // 種類ごとに最新（次回期限が最大）の記録を抽出
  const latestByType = new Map<string, VaccinationRecord>();
  for (const record of records) {
    if (!record.nextDueDate) continue;
    const existing = latestByType.get(record.type);
    if (!existing || !existing.nextDueDate) {
      latestByType.set(record.type, record);
      continue;
    }
    if (record.nextDueDate.toMillis() > existing.nextDueDate.toMillis()) {
      latestByType.set(record.type, record);
    }
  }

  let best: VaccinationAlert = none;
  for (const record of latestByType.values()) {
    if (!record.nextDueDate) continue;
    const { level, daysUntilDue } = getAlertLevelFromDueDate(
      record.nextDueDate.toDate(),
      today
    );
    if (level === 'none') continue;

    // overdue を最優先。同レベルなら期限が近い（daysUntilDue が小さい）方を採用
    const isMoreSevere =
      best.level === 'none' ||
      (level === 'overdue' && best.level === 'upcoming') ||
      (level === best.level && daysUntilDue < best.daysUntilDue);

    if (isMoreSevere) {
      best = { level, daysUntilDue, record };
    }
  }

  return best;
};

/**
 * アラートレベルのラベル
 */
export const getAlertLabel = (level: VaccinationAlertLevel): string => {
  switch (level) {
    case 'overdue':
      return '期限切れ';
    case 'upcoming':
      return '期限間近';
    default:
      return '';
  }
};

/**
 * アラートレベルの色（Chakra colorPalette）
 */
export const getAlertColor = (level: VaccinationAlertLevel): string => {
  switch (level) {
    case 'overdue':
      return 'red';
    case 'upcoming':
      return 'yellow';
    default:
      return 'gray';
  }
};
